import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import ProductManager from './ProductManager';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const { user, signOut } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [stats, setStats] = useState({
        totalProducts: 0,
        totalOrders: 0,
        pendingOrders: 0,
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const [allOrders, setAllOrders] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState('');
    const [editingStatus, setEditingStatus] = useState({});
    const [expandedOrders, setExpandedOrders] = useState({});
    const PAYBILL_NUMBER = '542542';
    const ACCOUNT_NUMBER = '07609030756150';
    const BANK_NAME = 'I&M Bank';

    const orderStatuses = ['pending_payment', 'paid', 'processing', 'shipped', 'delivered', 'cancelled', 'payment_failed'];

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setIsSidebarOpen(false); // Close sidebar on mobile when tab changes
    }

    useEffect(() => {
        fetchStats();
        fetchRecentOrders();
    }, []);

    useEffect(() => {
        if (activeTab !== 'orders') return;
        fetchAllOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const fetchStats = async () => {
        const { count: productCount } = await supabase
            .from('products')
            .select('*', { count: 'exact', head: true });

        const { count: orderCount } = await supabase
            .from('orders')
            .select('*', { count: 'exact', head: true });

        const { count: pendingCount } = await supabase
            .from('orders')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'pending');

        setStats({
            totalProducts: productCount || 0,
            totalOrders: orderCount || 0,
            pendingOrders: pendingCount || 0,
        });
    };

    const fetchRecentOrders = async () => {
        const { data } = await supabase
            .from('orders')
            .select('*')
            .order('created_at', { ascending: false })
            .limit(5);

        setRecentOrders(data || []);
    };

    const fetchAllOrders = async () => {
        setOrdersLoading(true);
        setOrdersError('');
        const { data, error } = await supabase
            .from('orders')
            .select('*')
            .order('created_at', { ascending: false });

        if (error) {
            setOrdersError(error.message || 'Failed to load orders');
            setAllOrders([]);
        } else {
            setAllOrders(data || []);
            setEditingStatus((prev) => {
                const next = { ...prev };
                (data || []).forEach((o) => {
                    if (next[o.id] == null) next[o.id] = o.status;
                });
                return next;
            });
        }
        setOrdersLoading(false);
    };

    const updateOrderStatus = async (orderId) => {
        const newStatus = editingStatus[orderId];
        if (!newStatus) return;

        const { error } = await supabase
            .from('orders')
            .update({ status: newStatus })
            .eq('id', orderId);

        if (error) {
            setOrdersError(error.message || 'Failed to update status');
            return;
        }

        setAllOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
        setRecentOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)));
        fetchStats();
    };

    const deleteOrder = async (orderId) => {
        if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
            return;
        }

        const { error } = await supabase
            .from('orders')
            .delete()
            .eq('id', orderId);

        if (error) {
            setOrdersError(error.message || 'Failed to delete order');
            return;
        }

        // Remove from state
        setAllOrders((prev) => prev.filter((o) => o.id !== orderId));
        setRecentOrders((prev) => prev.filter((o) => o.id !== orderId));
        fetchStats();
    };

    const toggleOrderDetails = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    const handleSignOut = async () => {
        await signOut();
        navigate('/admin/login');
    };

    return (
        <div className="admin-dashboard">
            <div className={`admin-sidebar-overlay ${isSidebarOpen ? 'open' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

            <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <h3>Admin Panel</h3>
                    <p>Welcome, Owner</p>
                </div>

                <nav className="sidebar-nav">
                    <button
                        className={activeTab === 'dashboard' ? 'active' : ''}
                        onClick={() => handleTabChange('dashboard')}
                    >
                        <i className="bi bi-grid-1x2-fill"></i> Dashboard
                    </button>
                    <button
                        className={activeTab === 'products' ? 'active' : ''}
                        onClick={() => handleTabChange('products')}
                    >
                        <i className="bi bi-box-seam"></i> Products
                    </button>
                    <button
                        className={activeTab === 'orders' ? 'active' : ''}
                        onClick={() => handleTabChange('orders')}
                    >
                        <i className="bi bi-bag-check"></i> Orders
                    </button>
                </nav>

                <div className="sidebar-footer">
                    <button onClick={handleSignOut} className="btn-signout">
                        <i className="bi bi-box-arrow-right"></i> Sign Out
                    </button>
                </div>
            </aside>

            <main className="admin-main">
                <header className="admin-topbar">
                    <button
                        className="mobile-menu-toggle d-md-none"
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                    >
                        <i className="bi bi-list"></i> Menu
                    </button>

                    <div className="admin-topbar-title">
                        {activeTab === 'dashboard' && <h2>Dashboard</h2>}
                        {activeTab === 'products' && <h2>Products</h2>}
                        {activeTab === 'orders' && <h2>Orders</h2>}
                    </div>

                    <div className="admin-topbar-right">
                        <span className="admin-user-email">{user?.email}</span>
                    </div>
                </header>

                <div className="admin-content">
                    {activeTab === 'dashboard' && (
                        <>
                            <div className="stats-grid">
                                <div className="stat-card">
                                    <div className="stat-icon products">
                                        <i className="bi bi-box-seam"></i>
                                    </div>
                                    <div className="stat-info">
                                        <h3>{stats.totalProducts}</h3>
                                        <p>Total Products</p>
                                    </div>
                                </div>

                                <div className="stat-card">
                                    <div className="stat-icon orders">
                                        <i className="bi bi-bag-check"></i>
                                    </div>
                                    <div className="stat-info">
                                        <h3>{stats.totalOrders}</h3>
                                        <p>Total Orders</p>
                                    </div>
                                </div>

                                <div className="stat-card">
                                    <div className="stat-icon pending">
                                        <i className="bi bi-clock-history"></i>
                                    </div>
                                    <div className="stat-info">
                                        <h3>{stats.pendingOrders}</h3>
                                        <p>Pending Orders</p>
                                    </div>
                                </div>
                            </div>

                            <div className="recent-orders">
                                <h3>Recent Orders</h3>
                                <div className="table-responsive">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>Order ID</th>
                                                <th>Customer</th>
                                                <th>Total</th>
                                                <th>Status</th>
                                                <th>Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentOrders.map(order => (
                                                <tr key={order.id}>
                                                    <td>#{order.id.substring(0, 8)}</td>
                                                    <td>{order.customer_name}</td>
                                                    <td>KSh {order.total_amount.toLocaleString()}</td>
                                                    <td><span className={`status-badge ${order.status}`}>{order.status}</span></td>
                                                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                                                </tr>
                                            ))}
                                            {recentOrders.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No orders found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}

                    {activeTab === 'products' && <ProductManager />}

                    {activeTab === 'orders' && (
                        <div className="orders-placeholder">
                            <div className="orders-header">
                                <h2>Order Management</h2>
                                <button className="btn-refresh" onClick={fetchAllOrders} disabled={ordersLoading}>
                                    <i className="bi bi-arrow-clockwise"></i> Refresh
                                </button>
                            </div>

                            {ordersError && <div className="error-message">{ordersError}</div>}

                            <div className="orders-list">
                                {allOrders.map(order => {
                                    const orderNo = order.items?.meta?.order_number || order.id.substring(0, 8).toUpperCase();
                                    const fulfillment = order.items?.fulfillment || {};
                                    const payment = order.items?.payment || {};
                                    const products = order.items?.products || [];
                                    const meta = order.items?.meta || {};
                                    const paymentMethodLabel = payment.method || (order.status === 'paid' ? 'M-Pesa STK Push' : 'M-Pesa Paybill');
                                    const isExpanded = expandedOrders[order.id];

                                    return (
                                        <div key={order.id} className="order-card">
                                            <div className="order-card-header" onClick={() => toggleOrderDetails(order.id)}>
                                                <div className="order-card-main">
                                                    <div className="order-info">
                                                        <h3>Order #{orderNo}</h3>
                                                        <p className="order-date">
                                                            {new Date(order.created_at).toLocaleDateString('en-US', {
                                                                year: 'numeric',
                                                                month: 'long',
                                                                day: 'numeric',
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </p>
                                                    </div>
                                                    <div className="order-summary">
                                                        <div className="order-customer">
                                                            <i className="bi bi-person-fill"></i>
                                                            <div>
                                                                <strong>{order.customer_name}</strong>
                                                                <p>{order.customer_phone}</p>
                                                            </div>
                                                        </div>
                                                        <div className="order-amount">
                                                            <span className="amount-label">Total</span>
                                                            <span className="amount-value">KSh {Number(order.total_amount || 0).toLocaleString()}</span>
                                                        </div>
                                                        <div className="order-status-section">
                                                            <select
                                                                value={editingStatus[order.id] || order.status}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingStatus((p) => ({ ...p, [order.id]: e.target.value }));
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className={`status-select ${editingStatus[order.id] || order.status}`}
                                                            >
                                                                {orderStatuses.map((s) => (
                                                                    <option key={s} value={s}>{s}</option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                className="btn-update-status-small"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    updateOrderStatus(order.id);
                                                                }}
                                                                disabled={ordersLoading}
                                                            >
                                                                <i className="bi bi-check-lg"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                <button className="btn-expand">
                                                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`}></i>
                                                </button>
                                            </div>

                                            {isExpanded && (
                                                <div className="order-card-details">
                                                    <div className="details-grid">
                                                        {/* Customer Details */}
                                                        <div className="detail-section">
                                                            <h4><i className="bi bi-person-circle"></i> Customer Information</h4>
                                                            <div className="detail-content">
                                                                <p><strong>Name:</strong> {order.customer_name}</p>
                                                                <p><strong>Phone:</strong> <a href={`tel:${order.customer_phone}`}>{order.customer_phone}</a></p>
                                                                {order.customer_email && (
                                                                    <p><strong>Email:</strong> <a href={`mailto:${order.customer_email}`}>{order.customer_email}</a></p>
                                                                )}
                                                                {meta.user_email && meta.user_email !== order.customer_email && (
                                                                    <p><strong>Account Email:</strong> {meta.user_email}</p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Delivery Details */}
                                                        <div className="detail-section">
                                                            <h4><i className="bi bi-truck"></i> Delivery Information</h4>
                                                            <div className="detail-content">
                                                                <p><strong>Type:</strong> {fulfillment.type || 'Delivery'}</p>
                                                                {fulfillment.county && <p><strong>County:</strong> {fulfillment.county}</p>}
                                                                {fulfillment.city && <p><strong>City:</strong> {fulfillment.city}</p>}
                                                                {fulfillment.address && <p><strong>Address:</strong> {fulfillment.address}</p>}
                                                                {fulfillment.delivery_time && <p><strong>Est. Time:</strong> {fulfillment.delivery_time}</p>}
                                                                {fulfillment.notes && (
                                                                    <p><strong>Notes:</strong> <em>{fulfillment.notes}</em></p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Payment Details */}
                                                        <div className="detail-section">
                                                            <h4><i className="bi bi-credit-card"></i> Payment Information</h4>
                                                            <div className="detail-content">
                                                                <p><strong>Method:</strong> {paymentMethodLabel}</p>
                                                                {order.status !== 'paid' && (
                                                                    <>
                                                                        <p><strong>Paybill Number:</strong> {payment.paybill || payment.till || PAYBILL_NUMBER}</p>
                                                                        <p><strong>Account Number:</strong> {payment.account || ACCOUNT_NUMBER}</p>
                                                                        <p><strong>Bank:</strong> {payment.bank || BANK_NAME}</p>
                                                                    </>
                                                                )}
                                                                {order.payment_reference && (
                                                                    <p><strong>M-Pesa Ref:</strong> <code>{order.payment_reference}</code></p>
                                                                )}
                                                                {order.payment_date && (
                                                                    <p><strong>Payment Date:</strong> {new Date(order.payment_date).toLocaleString()}</p>
                                                                )}
                                                                {order.checkout_request_id && (
                                                                    <p><strong>Checkout ID:</strong> <small>{order.checkout_request_id}</small></p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Order Summary */}
                                                        <div className="detail-section">
                                                            <h4><i className="bi bi-receipt"></i> Order Summary</h4>
                                                            <div className="detail-content">
                                                                <p><strong>Subtotal:</strong> KSh {(meta.subtotal || 0).toLocaleString()}</p>
                                                                <p><strong>Delivery Fee:</strong> KSh {(meta.delivery_fee || 0).toLocaleString()}</p>
                                                                <p className="total-line"><strong>Total:</strong> <strong>KSh {(meta.total || order.total_amount || 0).toLocaleString()}</strong></p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Order Items */}
                                                    <div className="detail-section full-width">
                                                        <h4><i className="bi bi-bag-check"></i> Order Items ({products.length})</h4>
                                                        <div className="order-items-list">
                                                            {products.map((item, idx) => (
                                                                <div key={idx} className="order-item">
                                                                    <img 
                                                                        src={item.productImg || '/assets/images/placeholder.png'} 
                                                                        alt={item.productName}
                                                                        onError={(e) => e.target.src = '/assets/images/user.png'}
                                                                    />
                                                                    <div className="item-info">
                                                                        <h5>{item.productName}</h5>
                                                                        <p>Qty: {item.productQuantity} × KSh {item.productPrice?.toLocaleString()}</p>
                                                                    </div>
                                                                    <div className="item-total">
                                                                        KSh {(item.productQuantity * item.productPrice).toLocaleString()}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Order Actions */}
                                                    <div className="detail-section full-width">
                                                        <div className="order-actions">
                                                            <button
                                                                className="btn-delete-order"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    deleteOrder(order.id);
                                                                }}
                                                                disabled={ordersLoading}
                                                            >
                                                                <i className="bi bi-trash"></i> Delete Order
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {!ordersLoading && allOrders.length === 0 && (
                                    <div className="no-orders">
                                        <i className="bi bi-inbox"></i>
                                        <p>No orders found</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
